import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import { Trash, Warning, DownloadSimple, CheckCircle } from "@phosphor-icons/react";

const LABELS = {
  users: "Users", jobs: "Jobs", projects: "Projects", tasks: "Tasks", clients: "Clients", suppliers: "Suppliers",
  quotes: "Quotes", invoices: "Invoices", contracts: "Contracts", uploaded_files: "Files", audit_logs: "Audit entries",
  notifications: "Notifications", messages: "Messages", work_orders: "Work orders", emergency_jobs: "Emergency jobs",
};
const pretty = (k) => LABELS[k] || k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

export async function downloadTenantExport(tenant) {
  const { data, headers } = await API.get(`/tenants/${tenant.id}/export.zip`, { responseType: "blob", timeout: 300000 });
  const match = /filename="?([^";]+)"?/.exec(headers["content-disposition"] || "");
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match?.[1] || `${tenant.slug || tenant.id}-export.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export function DeleteTenantDialog({ tenant, onClose, onDeleted }) {
  const [preview, setPreview] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exportState, setExportState] = useState("idle");

  const tenantId = tenant?.id;
  useEffect(() => {
    if (!tenantId) return;
    setPreview(null);
    setConfirm("");
    setExportState("idle");
    API.get(`/tenants/${tenantId}/deletion-preview`)
      .then(({ data }) => setPreview(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load deletion preview"));
  }, [tenantId]);

  const handleExport = async () => {
    setExportState("busy");
    try {
      await downloadTenantExport(tenant);
      setExportState("done");
      toast.success("Export downloaded — keep it somewhere safe before deleting");
    } catch (e) {
      setExportState("idle");
      toast.error(e.response?.data?.detail || "Export failed");
    }
  };

  const matches = preview && confirm.trim() === preview.confirm_with;

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { data } = await API.delete(`/tenants/${tenant.id}`, { data: { confirm: confirm.trim() } });
      toast.success(`${tenant.name} deleted — ${data.total_docs} records removed`);
      if (data.stripe_subscription_id) toast.warning(`Cancel Stripe subscription ${data.stripe_subscription_id} manually in the Stripe dashboard`);
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deletion failed");
    } finally {
      setDeleting(false);
    }
  };

  const top = preview ? Object.entries(preview.collections).sort((a, b) => b[1] - a[1]).slice(0, 12) : [];

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && !deleting && onClose()}>
      <DialogContent className="max-w-lg" data-testid="delete-tenant-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><Warning size={20} weight="fill" /> Permanently delete {tenant?.name}</DialogTitle>
          <DialogDescription>
            This removes the tenant, every user account and all of their data. It cannot be undone — suspend the tenant instead if you may need it back.
          </DialogDescription>
        </DialogHeader>
        {!preview ? (
          <div className="py-6 text-center text-sm text-muted-foreground" data-testid="delete-preview-loading">Counting records…</div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md border border-red-200 dark:border-red-900 bg-red-50/60 dark:bg-red-950/20 p-3">
              <p className="text-xs font-semibold text-red-700 dark:text-red-300 mb-2" data-testid="delete-total-docs">
                {preview.total_docs.toLocaleString()} records across {Object.keys(preview.collections).length} collections · {preview.users} user{preview.users !== 1 ? "s" : ""}
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]" data-testid="delete-preview-breakdown">
                {top.map(([k, v]) => (
                  <div key={k} className="flex justify-between"><span className="text-muted-foreground">{pretty(k)}</span><span className="font-mono font-semibold">{v}</span></div>
                ))}
              </div>
              {preview.local_dirs.length > 0 && <p className="text-[10px] text-muted-foreground mt-2">Uploaded files on disk will also be removed.</p>}
              {preview.tenant.stripe_subscription_id && (
                <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-2" data-testid="delete-stripe-warning">
                  Active Stripe subscription {preview.tenant.stripe_subscription_id} — cancel it in Stripe after deleting.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-border p-3" data-testid="delete-export-row">
              <div className="min-w-0">
                <p className="text-xs font-semibold">Download a full export first</p>
                <p className="text-[10px] text-muted-foreground">ZIP of every record (JSON per collection) plus uploaded files.</p>
              </div>
              <Button variant={exportState === "done" ? "secondary" : "outline"} size="sm" className="shrink-0 h-8 text-xs gap-1" onClick={handleExport} disabled={exportState === "busy" || deleting} data-testid="delete-export-btn">
                {exportState === "done" ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : <DownloadSimple size={14} />}
                {exportState === "busy" ? "Building ZIP…" : exportState === "done" ? "Exported" : "Export ZIP"}
              </Button>
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-mono font-bold">{preview.confirm_with}</span> to confirm</Label>
              <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 font-mono" autoComplete="off" placeholder={preview.confirm_with} data-testid="delete-confirm-input" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={onClose} disabled={deleting} data-testid="delete-cancel-btn">Keep tenant</Button>
              <Button variant="destructive" className="flex-1" onClick={handleDelete} disabled={!matches || deleting} data-testid="delete-confirm-btn">
                <Trash size={14} className="mr-1" /> {deleting ? "Deleting…" : "Delete everything"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
